import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { Phone } from "lucide-react";

import { ContentProvider, useContent } from "@/ContentContext";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Ticker from "@/components/Ticker";
import About from "@/components/About";
import Services from "@/components/Services";
import Gallery from "@/components/Gallery";
import Areas from "@/components/Areas";
import Testimonials from "@/components/Testimonials";
import Blog from "@/components/Blog";
import EmergencyBanner from "@/components/EmergencyBanner";
import FAQ from "@/components/FAQ";
import Contact from "@/components/Contact";
import StickyCTA from "@/components/StickyCTA";
import QuoteDialog from "@/components/QuoteDialog";
import BlogPost from "@/components/BlogPost";
import { Privacy, Terms } from "@/components/Legal";
import AdminApp from "@/admin/AdminApp";
import StudioApp from "@/studio/StudioApp";
import SEO from "@/components/SEO";
import {
  EmergencyTreeServicePage,
  TreeRemovalPage,
  StormDamageTreeRemovalPage,
  TreeTrimmingPage,
} from "@/pages/ServicePages";

function TopBar() {
  const { company } = useContent();
  return (
    <div className="w-full text-center py-2 text-xs font-medium tracking-wide" style={{ background: "var(--canopy)", color: "var(--moss)" }}>
      {company.promo ? company.promo : <>
        {company.licensed_insured ? "Licensed & Insured · " : ""}{company.emergency_247 ? "24/7 Emergency Tree Service · " : ""}
        <a href={`tel:${company.phoneRaw}`} className="inline-flex items-center gap-1 font-semibold" style={{ color: "var(--brass)" }}>
          <Phone size={12} /> {company.phone}
        </a>
      </>}
    </div>
  );
}

function Home() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("estimate");
  const openDialog = (m) => { setMode(m); setOpen(true); };

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div style={{ background: "var(--bone)" }}>
      <SEO
        title="Tree Service Huntsville AL"
        description="Professional tree service in Huntsville, AL for tree removal, trimming, storm damage and emergency tree service. Serving Huntsville and Madison. Request a free estimate today."
        path="/"
      />
      <TopBar />
      <Nav onEstimate={() => openDialog("estimate")} />
      <Hero onEstimate={() => openDialog("estimate")} onSchedule={() => openDialog("schedule")} />
      <Ticker />
      <About />
      <Services onEstimate={() => openDialog("estimate")} />
      <EmergencyBanner />
      <Gallery />
      <Areas />
      <Testimonials />
      <Blog />
      <FAQ />
      <Contact onEstimate={() => openDialog("estimate")} />
      <StickyCTA onEstimate={() => openDialog("estimate")} />
      <QuoteDialog open={open} onOpenChange={setOpen} mode={mode} />
    </div>
  );
}

function App() {
  // When served on the Yosi Media Studio hostname, the Studio IS the site root.
  // This lets `portal.yosimediastudio.com` render the Agency Portal at `/` while
  // keeping `topofthelinetreeservicesllc.com/studio` working during setup.
  const isStudioHost = typeof window !== "undefined" &&
    /(^|\.)(yosimediastudio\.com)$/i.test(window.location.hostname);

  if (isStudioHost) {
    return (
      <div className="App">
        <HelmetProvider>
          <BrowserRouter>
            <Routes>
              <Route path="*" element={<StudioApp />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </HelmetProvider>
      </div>
    );
  }

  return (
    <div className="App">
      <HelmetProvider>
        <ContentProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/emergency-tree-service-huntsville-al" element={<EmergencyTreeServicePage />} />
              <Route path="/tree-removal-huntsville-al" element={<TreeRemovalPage />} />
              <Route path="/storm-damage-tree-removal-huntsville-al" element={<StormDamageTreeRemovalPage />} />
              <Route path="/tree-trimming-huntsville-al" element={<TreeTrimmingPage />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/admin" element={<AdminApp />} />
              <Route path="/studio" element={<StudioApp />} />
            </Routes>
          </BrowserRouter>
        </ContentProvider>
        <Toaster position="top-center" richColors />
      </HelmetProvider>
    </div>
  );
}

export default App;
